import { computed, onScopeDispose, watch } from "vue";
import { equalFinderData } from "#ce/components/SpFinder/engine/finder-data";
import { useFinder } from "#ce/composables/useFinder";
import { FINDER_NOT_FOUND } from "#ce/shared/finder-association";
import { projectFinderScope } from "#ce/components/SpFinder/engine/finder-option-scope";
import { toJSON } from "#ce/utils/props";

/**
 * Projects option scope and retries an equal publication only after its previous attempt failed.
 * @param {Object} props - The filter's scope, dependencies, and finder association props.
 * @param {Object} [options] - Request behavior outside a finder.
 * @param {boolean} [options.allowStandalone=false] - Allows unscoped requests when no enclosing finder is found.
 */
export function useFinderOptionScope(props, { allowStandalone = false } = {}) {
  const finder = useFinder({
    finder: () => props.finder,
  });
  const standalone = computed(
    () => props.scope !== undefined || (allowStandalone && finder.associationError.value === FINDER_NOT_FOUND),
  );
  const enabled = computed(
    () => standalone.value || (finder.connected.value && finder.filterValues.value !== undefined),
  );
  const scope = computed(() =>
    standalone.value ? toJSON(props.scope, true) : projectFinderScope(finder.filterValues.value, props.filterScopes),
  );
  let applied;
  let generation = 0;

  function connect(apply) {
    async function update() {
      if (!enabled.value) {
        applied = undefined;
        generation += 1;

        return;
      }

      const next = scope.value;

      if (applied !== undefined && equalFinderData(next, applied)) {
        return;
      }

      applied = next;

      const request = ++generation;

      try {
        const success = await apply(next);

        if (success === false && request === generation) {
          applied = undefined;
        }
      } catch {
        if (request === generation) {
          applied = undefined;
        }
      }
    }

    watch([enabled, scope, finder.publication], update, {
      immediate: true,
    });
  }

  function dispose() {
    generation += 1;
  }

  onScopeDispose(dispose);

  return {
    enabled,
    connect,
    finder,
  };
}
